import type { TFunction } from "i18next";

/**
 * Compact "time ago" string used across tables and KPI pills:
 * "3m ago", "2d ago", "1y ago". Single source for the People table, Invites
 * table, and dashboard KPI highlight pills, which previously each carried a
 * byte-identical copy of this ladder.
 */
export function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return "just now";
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/**
 * i18n variant of `relativeTime` for user-facing copy that must be
 * translated (Person drawer "last seen" line, Orgs table "created" column).
 */
export function relativeTimeI18n(isoString: string, t: TFunction): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return t("iam.relativeTime.justNow");
  const m = Math.floor(s / 60);
  if (m < 60) return t("iam.relativeTime.minutesAgo", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("iam.relativeTime.hoursAgo", { count: h });
  const d = Math.floor(h / 24);
  if (d < 30) return t("iam.relativeTime.daysAgo", { count: d });
  return t("iam.relativeTime.monthsAgo", { count: Math.floor(d / 30) });
}

/** Absolute "Mon D, YYYY, HH:MM" timestamp used in tooltips (invite sent/expiry). */
export function fullTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

/**
 * Audit-log specific relative time: minutes/hours "ago", then
 * "Yesterday HH:MM", then "Mon D HH:MM" (UTC) beyond that. Kept distinct from
 * `relativeTime` because the audit trail needs an absolute anchor once an
 * entry is more than a day old, not an ever-growing "Xd ago".
 */
export function relativeTimeAudit(isoString: string): string {
  const now = new Date();
  const ts = new Date(isoString);
  const diffMs = now.getTime() - ts.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const hh = ts.getUTCHours().toString().padStart(2, "0");
  const mm = ts.getUTCMinutes().toString().padStart(2, "0");
  if (diffHrs < 48) return `Yesterday ${hh}:${mm}`;
  const mon = ts.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return `${mon} ${ts.getUTCDate()} ${hh}:${mm}`;
}

/**
 * Longer-range "time ago" that falls back to an absolute date past 30 days.
 * Used for the Roles table/drawer "Updated" columns, which care more about
 * an exact date than a growing day count.
 */
export function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;

  if (diff < 2 * min) return "just now";
  if (diff < hour) return `${Math.floor(diff / min)} min ago`;
  if (diff < 2 * hour) return "1 h ago";
  if (diff < day) return `${Math.floor(diff / hour)} h ago`;
  if (diff < 2 * day) return "1 day ago";
  if (diff < 30 * day) return `${Math.floor(diff / day)} days ago`;

  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: diff > 365 * day ? "numeric" : undefined,
  });
}
